/**
 * `settlement_event.delivery_reference` readers (#3030).
 *
 * The attribute is MIXED-SHAPE by design, and the ontology declares it that way
 * (`string_or_json_array`), because it has two writers with irreconcilable
 * cardinality:
 *
 *  - the document/OCR lane writes the ONE printed Lieferschein-Nr. a Gutschrift
 *    names, as a bare string;
 *  - the wibill/native lane writes the FULL set of Lieferscheine one invoice
 *    settles (`lieferscheine.Rnr` names the invoice on 98,3 % of the WF pilot's
 *    Lieferscheine), as an array.
 *
 * Every FE read predated the array and reached for `as string` / `String(...)`.
 * On an array `String(['LF1','LF2'])` yields `'LF1,LF2'` — a plausible-looking
 * string that matches no Lieferschein card, so `MatchGraph` silently anchored
 * the GS→LS arrow to whichever card happened to be first. These helpers exist so
 * the coercion happens ONCE, deliberately, instead of at five call sites.
 *
 * Mirrors the worker-side readers so the two ends of the pipeline cannot drift:
 * `gutschrift_match_group._delivery_refs` and
 * `rematerialize_index._delivery_reference_values`.
 */

/** Every reference the event cites, blank/nullish tokens dropped. */
export function deliveryReferences(event: Record<string, unknown> | null | undefined): string[] {
  const raw = event?.delivery_reference;
  if (raw == null) return [];
  const values = Array.isArray(raw) ? raw : [raw];
  return values
    .filter((value): value is NonNullable<typeof value> => value != null)
    .map((value) => String(value).trim())
    .filter((value) => value.length > 0);
}

/**
 * The references as ONE display string, or `null` when the event cites none.
 *
 * Joined with `', '` rather than truncated to the first token: on an orphan
 * settlement the placeholder header and the Ref chip are the operator's only
 * lead for hunting down the missing Lieferschein, so dropping the other N − 1
 * numbers would throw away exactly the information they need. `null` (not `''`)
 * so callers keep their existing "cites nothing" branch.
 */
export function deliveryReferenceLabel(
  event: Record<string, unknown> | null | undefined,
): string | null {
  const refs = deliveryReferences(event);
  return refs.length > 0 ? refs.join(', ') : null;
}

/**
 * The first cited reference that resolves to a rendered Lieferschein card.
 *
 * A multi-Lieferschein Gutschrift has N candidates and only the ones present in
 * THIS component can be anchored to. Returning the first resolvable one keeps
 * the arrow pointing at a document the GS genuinely cites, where
 * `String(delivery_reference)` used to miss the map entirely and fall through to
 * "whichever card is first".
 */
export function firstResolvableReference(
  event: Record<string, unknown> | null | undefined,
  isResolvable: (ref: string) => boolean,
): string | null {
  return deliveryReferences(event).find(isResolvable) ?? null;
}
